import React from 'react';
import NextImage, { StaticImageData } from 'next/image';
import { useTheme } from 'styled-components';
import useBreakpoint from 'Common/hooks/useBreakpoint';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import FlexBox from 'Elements/FlexBox';
import ButtonIcon, { IconButtonProps } from 'Elements/Button/Icon';
import { H5 } from 'Elements/Typography';

import Grid, { Col } from 'Components/Grid';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import IconListColumns, { IconListColumnsProps } from 'Components/List/IconList/Columns';
import AccommodationCard, { AccommodationCardProps } from 'Components/Card/Accommodation';

import MakeWavesHeroBanner, { MakeWavesHeroBannerProps } from 'Modules/HeroBanner/MakeWaves';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: MakeWavesHeroBannerProps;
    whatsIncludedHeading: string;
    whatsIncludedList: IconListColumnsProps;
    studentAccommodationImage: {
        alt?: string;
        src: StaticImageData | string;
    };
    otherOptionsHeading: string;
    otherOptionsBtns: IconButtonProps[];
    accommodationCards: AccommodationCardProps[];
}

const Listing: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        whatsIncludedHeading,
        whatsIncludedList,
        studentAccommodationImage,
        otherOptionsHeading,
        otherOptionsBtns,
        accommodationCards
    } = props;

    const { breakpoints } = useTheme();
    const isTablet = !useBreakpoint(breakpoints.large.value);

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <MakeWavesHeroBanner {...heroBanner} />
            <Grid>
                <Col l={4} order={isTablet ? 2 : 1}>
                    <aside>
                        <Grid>
                            <Col order={1}>
                                <Section id="what-is-included" paddingBottom="none">
                                    <H5 as="h2">{whatsIncludedHeading}</H5>
                                    <IconListColumns {...whatsIncludedList} />
                                </Section>
                            </Col>
                            <Col
                                m={6}
                                l={12}
                                order={isTablet ? 3 : 2}
                                textAlign={isTablet ? 'center' : undefined}
                                align="center"
                            >
                                <NextImage
                                    {...studentAccommodationImage}
                                    alt={
                                        studentAccommodationImage.alt ||
                                        'The Student Accommodation Code logo'
                                    }
                                    width={230}
                                    height={130}
                                />
                            </Col>
                            <Col m={6} l={12} order={isTablet ? 2 : 3}>
                                <Section id="other-options" paddingTop={0.5}>
                                    <H5
                                        as="h2"
                                        spacing={{
                                            base: { margin: '0 0 1rem' },
                                            large: { margin: '0 0 1rem' }
                                        }}
                                    >
                                        {otherOptionsHeading}
                                    </H5>
                                    <FlexBox gap="1rem" direction="column">
                                        {otherOptionsBtns.map(button => (
                                            <ButtonIcon key={button.id} {...button} />
                                        ))}
                                    </FlexBox>
                                </Section>
                            </Col>
                        </Grid>
                    </aside>
                </Col>
                <Col l={8} order={isTablet ? 1 : 2}>
                    <Section id="residence_listing" paddingBottom={isTablet ? 'none' : undefined}>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {accommodationCards.map((card, index) => (
                                <li key={index} style={{ marginBottom: '1.5rem' }}>
                                    <AccommodationCard {...card} />
                                </li>
                            ))}
                        </ul>
                    </Section>
                </Col>
            </Grid>
        </Main>
    );
};

export default Listing;
